/**
 * Vendor overlay display helpers.
 *
 * The backend serves the coverage matrix via:
 *   GET /api/zero-trust/vendors?q=<search>
 *   GET /api/zero-trust/vendors/:vendorId/overlay
 *
 * Display metadata (colors, aliases) lives here in the frontend for now —
 * the DB doesn't store it. Can migrate to DB later if the admin UI wants
 * to edit it.
 */

// Known vendor display metadata. Vendors not listed here get DEFAULT_OVERLAY_COLOR.
// aliases boost client-side search matching against the picker input.
// logoUrl uses Clearbit's Logo API for consistent, high-quality vendor logos.
const VENDOR_METADATA = {
  // === Core ZT Vendors (frequently used) ===
  'Cisco':       { displayColor: '#049fd9', logoUrl: 'https://logo.clearbit.com/cisco.com', aliases: ['Cisco Systems', 'Cisco + Splunk'], isPreset: true },
  'Microsoft':   { displayColor: '#e81123', logoUrl: 'https://logo.clearbit.com/microsoft.com', aliases: ['MSFT', 'MS'] },
  'Palo Alto':   { displayColor: '#fa582d', logoUrl: 'https://logo.clearbit.com/paloaltonetworks.com', aliases: ['Palo Alto Networks', 'PAN', 'PANW'] },
  'CrowdStrike': { displayColor: '#ef4444', logoUrl: 'https://logo.clearbit.com/crowdstrike.com', aliases: ['CRWD'] },
  'Fortinet':    { displayColor: '#ee3124', logoUrl: 'https://logo.clearbit.com/fortinet.com', aliases: ['FTNT'] },
  'Okta':        { displayColor: '#007dc1', logoUrl: 'https://logo.clearbit.com/okta.com' },
  'Zscaler':     { displayColor: '#00bceb', logoUrl: 'https://logo.clearbit.com/zscaler.com', aliases: ['ZS'] },
  'Splunk':      { displayColor: '#f99d1c', logoUrl: 'https://logo.clearbit.com/splunk.com' },
  'Elastic':     { displayColor: '#00bfb3', logoUrl: 'https://logo.clearbit.com/elastic.co', aliases: ['Elasticsearch', 'ELK'] },
  'Cloudflare':  { displayColor: '#f48120', logoUrl: 'https://logo.clearbit.com/cloudflare.com' },
  'SailPoint':   { displayColor: '#1c4caf', logoUrl: 'https://logo.clearbit.com/sailpoint.com' },
  'HashiCorp':   { displayColor: '#4040b2', logoUrl: 'https://logo.clearbit.com/hashicorp.com' },
  'Google':      { displayColor: '#4285f4', logoUrl: 'https://logo.clearbit.com/google.com' },
  'SentinelOne': { displayColor: '#6b21a8', logoUrl: 'https://logo.clearbit.com/sentinelone.com' },
  'VMware':      { displayColor: '#607078', logoUrl: 'https://logo.clearbit.com/vmware.com' },
  'Netskope':    { displayColor: '#1d4ed8', logoUrl: 'https://logo.clearbit.com/netskope.com' },
  'Akamai':      { displayColor: '#0074c3', logoUrl: 'https://logo.clearbit.com/akamai.com' },
  'Illumio':     { displayColor: '#f59e0b', logoUrl: 'https://logo.clearbit.com/illumio.com' },
  'Darktrace':   { displayColor: '#5b21b6', logoUrl: 'https://logo.clearbit.com/darktrace.com' },
  'Tanium':      { displayColor: '#0ea5e9', logoUrl: 'https://logo.clearbit.com/tanium.com' },
  'Varonis':     { displayColor: '#2563eb', logoUrl: 'https://logo.clearbit.com/varonis.com' },
  'CyberArk':    { displayColor: '#0f766e', logoUrl: 'https://logo.clearbit.com/cyberark.com' },

  // === Cloud Providers ===
  'AWS':         { displayColor: '#ff9900', logoUrl: 'https://logo.clearbit.com/aws.amazon.com', aliases: ['Amazon Web Services', 'Amazon'] },

  // === Identity & Access ===
  'Ping Identity': { displayColor: '#2e8b57', logoUrl: 'https://logo.clearbit.com/pingidentity.com', aliases: ['PingOne', 'Ping'] },
  'OneLogin':    { displayColor: '#00bcd4', logoUrl: 'https://logo.clearbit.com/onelogin.com' },
  'JumpCloud':   { displayColor: '#00c853', logoUrl: 'https://logo.clearbit.com/jumpcloud.com' },
  'ForgeRock':   { displayColor: '#f9a825', logoUrl: 'https://logo.clearbit.com/forgerock.com' },
  'RSA':         { displayColor: '#dc2626', logoUrl: 'https://logo.clearbit.com/rsa.com', aliases: ['RSA Security'] },
  'Thales':      { displayColor: '#00599c', logoUrl: 'https://logo.clearbit.com/thalesgroup.com' },
  'Entrust':     { displayColor: '#c41230', logoUrl: 'https://logo.clearbit.com/entrust.com' },
  'HYPR':        { displayColor: '#00d4ff', logoUrl: 'https://logo.clearbit.com/hypr.com' },
  'Yubico':      { displayColor: '#84cc16', logoUrl: 'https://logo.clearbit.com/yubico.com', aliases: ['YubiKey'] },
  'Saviynt':     { displayColor: '#f97316', logoUrl: 'https://logo.clearbit.com/saviynt.com' },
  'One Identity': { displayColor: '#6366f1', logoUrl: 'https://logo.clearbit.com/oneidentity.com' },
  'Omada':       { displayColor: '#10b981', logoUrl: 'https://logo.clearbit.com/omada.net' },
  'BeyondTrust': { displayColor: '#f97316', logoUrl: 'https://logo.clearbit.com/beyondtrust.com' },
  'Delinea':     { displayColor: '#8b5cf6', logoUrl: 'https://logo.clearbit.com/delinea.com' },
  'Teleport':    { displayColor: '#512fc9', logoUrl: 'https://logo.clearbit.com/goteleport.com' },
  'StrongDM':    { displayColor: '#14b8a6', logoUrl: 'https://logo.clearbit.com/strongdm.com' },
  'Radiant Logic': { displayColor: '#3b82f6', logoUrl: 'https://logo.clearbit.com/radiantlogic.com' },
  'Securonix':   { displayColor: '#f59e0b', logoUrl: 'https://logo.clearbit.com/securonix.com' },
  'Exabeam':     { displayColor: '#3b82f6', logoUrl: 'https://logo.clearbit.com/exabeam.com' },
  'LogRhythm':   { displayColor: '#0ea5e9', logoUrl: 'https://logo.clearbit.com/logrhythm.com' },
  'Sumo Logic':  { displayColor: '#000daa', logoUrl: 'https://logo.clearbit.com/sumologic.com' },

  // === Endpoint & MDM ===
  'Jamf':        { displayColor: '#62a8e8', logoUrl: 'https://logo.clearbit.com/jamf.com' },
  'IBM':         { displayColor: '#0f62fe', logoUrl: 'https://logo.clearbit.com/ibm.com' },
  'Ivanti':      { displayColor: '#ff5722', logoUrl: 'https://logo.clearbit.com/ivanti.com' },
  'Citrix':      { displayColor: '#0e4aa0', logoUrl: 'https://logo.clearbit.com/citrix.com' },
  'Kandji':      { displayColor: '#1a73e8', logoUrl: 'https://logo.clearbit.com/kandji.io' },
  'SOTI':        { displayColor: '#00a0df', logoUrl: 'https://logo.clearbit.com/soti.net' },
  'ManageEngine': { displayColor: '#dc3c28', logoUrl: 'https://logo.clearbit.com/manageengine.com' },
  'Qualys':      { displayColor: '#ed1c24', logoUrl: 'https://logo.clearbit.com/qualys.com' },
  'Tenable':     { displayColor: '#00a1f1', logoUrl: 'https://logo.clearbit.com/tenable.com' },
  'Rapid7':      { displayColor: '#ff6633', logoUrl: 'https://logo.clearbit.com/rapid7.com' },
  'Tripwire':    { displayColor: '#00b2a9', logoUrl: 'https://logo.clearbit.com/tripwire.com' },
  'HCL':         { displayColor: '#0073e6', logoUrl: 'https://logo.clearbit.com/hcltechsw.com' },
  'Cybereason':  { displayColor: '#6366f1', logoUrl: 'https://logo.clearbit.com/cybereason.com' },
  'Trellix':     { displayColor: '#ff5500', logoUrl: 'https://logo.clearbit.com/trellix.com' },
  'Sophos':      { displayColor: '#003b75', logoUrl: 'https://logo.clearbit.com/sophos.com' },
  'Trend Micro': { displayColor: '#d71920', logoUrl: 'https://logo.clearbit.com/trendmicro.com' },
  'Axonius':     { displayColor: '#06b6d4', logoUrl: 'https://logo.clearbit.com/axonius.com' },
  'ServiceNow':  { displayColor: '#81b5a1', logoUrl: 'https://logo.clearbit.com/servicenow.com' },
  'Oomnitza':    { displayColor: '#4ade80', logoUrl: 'https://logo.clearbit.com/oomnitza.com' },
  'Flexera':     { displayColor: '#dc2626', logoUrl: 'https://logo.clearbit.com/flexera.com' },
  'Lansweeper': { displayColor: '#2563eb', logoUrl: 'https://logo.clearbit.com/lansweeper.com' },
  'runZero':     { displayColor: '#8b5cf6', logoUrl: 'https://logo.clearbit.com/runzero.com' },
  'JupiterOne':  { displayColor: '#22c55e', logoUrl: 'https://logo.clearbit.com/jupiterone.com' },
  'Cato Networks': { displayColor: '#10b981', logoUrl: 'https://logo.clearbit.com/catonetworks.com' },
  'Appgate':     { displayColor: '#1e3a8a', logoUrl: 'https://logo.clearbit.com/appgate.com' },

  // === Network & Security ===
  'Check Point': { displayColor: '#e22d24', logoUrl: 'https://logo.clearbit.com/checkpoint.com' },
  'Juniper':     { displayColor: '#84cc16', logoUrl: 'https://logo.clearbit.com/juniper.net' },
  'Arista':      { displayColor: '#0ea5e9', logoUrl: 'https://logo.clearbit.com/arista.com' },
  'Aruba':       { displayColor: '#ff8300', logoUrl: 'https://logo.clearbit.com/arubanetworks.com' },
  'Forescout':   { displayColor: '#004c97', logoUrl: 'https://logo.clearbit.com/forescout.com' },
  'Vectra AI':   { displayColor: '#00c7b7', logoUrl: 'https://logo.clearbit.com/vectra.ai' },
  'ExtraHop':    { displayColor: '#00b4d8', logoUrl: 'https://logo.clearbit.com/extrahop.com' },
  'Corelight':   { displayColor: '#f97316', logoUrl: 'https://logo.clearbit.com/corelight.com' },
  'Gigamon':     { displayColor: '#f59e0b', logoUrl: 'https://logo.clearbit.com/gigamon.com' },
  'ColorTokens': { displayColor: '#ef4444', logoUrl: 'https://logo.clearbit.com/colortokens.com' },
  'Radware':     { displayColor: '#dc2626', logoUrl: 'https://logo.clearbit.com/radware.com' },
  'NETSCOUT':    { displayColor: '#0047ab', logoUrl: 'https://logo.clearbit.com/netscout.com' },

  // === Application Security ===
  'F5':          { displayColor: '#e4002b', logoUrl: 'https://logo.clearbit.com/f5.com' },
  'Imperva':     { displayColor: '#005eb8', logoUrl: 'https://logo.clearbit.com/imperva.com' },
  'Barracuda':   { displayColor: '#00a4ef', logoUrl: 'https://logo.clearbit.com/barracuda.com' },
  'Kong':        { displayColor: '#003459', logoUrl: 'https://logo.clearbit.com/konghq.com' },
  'Contrast':    { displayColor: '#5b21b6', logoUrl: 'https://logo.clearbit.com/contrastsecurity.com', aliases: ['Contrast Security'] },
  'Dynatrace':   { displayColor: '#1496ff', logoUrl: 'https://logo.clearbit.com/dynatrace.com' },
  'Fastly':      { displayColor: '#ff282d', logoUrl: 'https://logo.clearbit.com/fastly.com', aliases: ['Signal Sciences'] },
  'Snyk':        { displayColor: '#4c4a73', logoUrl: 'https://logo.clearbit.com/snyk.io' },
  'Veracode':    { displayColor: '#00b4e6', logoUrl: 'https://logo.clearbit.com/veracode.com' },
  'Checkmarx':   { displayColor: '#54b848', logoUrl: 'https://logo.clearbit.com/checkmarx.com' },
  'SonarSource': { displayColor: '#4c9bd6', logoUrl: 'https://logo.clearbit.com/sonarsource.com', aliases: ['SonarQube'] },
  'Synopsys':    { displayColor: '#7d00b5', logoUrl: 'https://logo.clearbit.com/synopsys.com', aliases: ['Black Duck'] },
  'Mend':        { displayColor: '#3b82f6', logoUrl: 'https://logo.clearbit.com/mend.io', aliases: ['WhiteSource'] },
  'Aqua Security': { displayColor: '#1d4ed8', logoUrl: 'https://logo.clearbit.com/aquasec.com' },
  'GitHub':      { displayColor: '#24292f', logoUrl: 'https://logo.clearbit.com/github.com' },
  'GitLab':      { displayColor: '#fc6d26', logoUrl: 'https://logo.clearbit.com/gitlab.com' },
  'JFrog':       { displayColor: '#40be46', logoUrl: 'https://logo.clearbit.com/jfrog.com' },
  'Semgrep':     { displayColor: '#16a34a', logoUrl: 'https://logo.clearbit.com/semgrep.dev' },

  // === Data Protection ===
  'BigID':       { displayColor: '#6366f1', logoUrl: 'https://logo.clearbit.com/bigid.com' },
  'Securiti':    { displayColor: '#f97316', logoUrl: 'https://logo.clearbit.com/securiti.ai' },
  'OneTrust':    { displayColor: '#006c5b', logoUrl: 'https://logo.clearbit.com/onetrust.com' },
  'Spirion':     { displayColor: '#00a0df', logoUrl: 'https://logo.clearbit.com/spirion.com' },
  'Titus':       { displayColor: '#0066b3', logoUrl: 'https://logo.clearbit.com/titus.com' },
  'Boldon James': { displayColor: '#1e40af', logoUrl: 'https://logo.clearbit.com/boldonjames.com' },
  'Fortra':      { displayColor: '#e11d48', logoUrl: 'https://logo.clearbit.com/fortra.com', aliases: ['Digital Guardian'] },
  'PKWARE':      { displayColor: '#2563eb', logoUrl: 'https://logo.clearbit.com/pkware.com' },
  'Symantec':    { displayColor: '#ffc000', logoUrl: 'https://logo.clearbit.com/broadcom.com', aliases: ['Broadcom'] },
  'Proofpoint':  { displayColor: '#004b87', logoUrl: 'https://logo.clearbit.com/proofpoint.com' },
  'Code42':      { displayColor: '#00b5ad', logoUrl: 'https://logo.clearbit.com/code42.com' },
  'DTEX':        { displayColor: '#f97316', logoUrl: 'https://logo.clearbit.com/dtexsystems.com' },
  'Fortanix':    { displayColor: '#6366f1', logoUrl: 'https://logo.clearbit.com/fortanix.com' },

  // === Backup & Recovery ===
  'Veeam':       { displayColor: '#00b336', logoUrl: 'https://logo.clearbit.com/veeam.com' },
  'Cohesity':    { displayColor: '#0a66c2', logoUrl: 'https://logo.clearbit.com/cohesity.com' },
  'Commvault':   { displayColor: '#f9a825', logoUrl: 'https://logo.clearbit.com/commvault.com' },
  'Rubrik':      { displayColor: '#00bcd4', logoUrl: 'https://logo.clearbit.com/rubrik.com' },
  'Druva':       { displayColor: '#1d4ed8', logoUrl: 'https://logo.clearbit.com/druva.com' },
  'Veritas':     { displayColor: '#c41230', logoUrl: 'https://logo.clearbit.com/veritas.com' },
  'Zerto':       { displayColor: '#e11d48', logoUrl: 'https://logo.clearbit.com/zerto.com' },
};

export const DEFAULT_OVERLAY_COLOR = '#3b82f6';
export const CISCO_PRESET_VENDOR_NAME = 'Cisco';

/**
 * Normalize an API vendor object into something the picker + overlay UI
 * can render. API shape: { vendor_id, vendor_name, verified_product_count }.
 */
export function enhanceVendor(apiVendor) {
  if (!apiVendor) return null;
  const meta = VENDOR_METADATA[apiVendor.vendor_name] || {};
  return {
    id: apiVendor.vendor_id,
    name: apiVendor.vendor_name,
    displayColor: meta.displayColor || DEFAULT_OVERLAY_COLOR,
    logoUrl: meta.logoUrl || null,
    aliases: meta.aliases || [],
    isPreset: !!meta.isPreset,
    verifiedProductCount: Number(apiVendor.verified_product_count) || 0,
  };
}

/**
 * Get vendor metadata by name (for local/diagram usage where we only have the name)
 * @param {string} vendorName - The vendor name
 * @returns {Object} Vendor metadata with displayColor and logoUrl
 */
export function getVendorMetadata(vendorName) {
  if (!vendorName) return { displayColor: DEFAULT_OVERLAY_COLOR, logoUrl: null };
  const meta = VENDOR_METADATA[vendorName] || {};
  return {
    displayColor: meta.displayColor || DEFAULT_OVERLAY_COLOR,
    logoUrl: meta.logoUrl || null,
    aliases: meta.aliases || [],
  };
}

/**
 * Build a multi-vendor coverage lookup so the ZT page can ask "which vendors
 * cover capability X, and at what strength?"
 *
 * Accepts an array of overlay responses (one per selected vendor) and returns:
 *   { [frontend_capability_id]: Array<{ vendor, productName, coverageLevel, notes }> }
 *
 * Within each capability the list is sorted by coverage strength
 * (STRONG > PARTIAL > GAP) so the picker/cell renderer can pick the first
 * entry as "primary" when needed.
 *
 * Cells with null frontend_capability_id are skipped. A vendor with multiple
 * products for the same capability is deduplicated to its strongest product.
 */
export function buildCoverageMap(overlays) {
  const list = Array.isArray(overlays) ? overlays : (overlays ? [overlays] : []);
  const map = {};
  for (const overlay of list) {
    if (!overlay || !Array.isArray(overlay.products)) continue;
    const vendor = overlay.vendor
      ? enhanceVendor({ vendor_id: overlay.vendor.vendor_id, vendor_name: overlay.vendor.vendor_name })
      : null;
    if (!vendor) continue;

    // Dedupe this vendor's multiple products for the same capability → strongest wins.
    const perCap = new Map();
    for (const p of overlay.products) {
      if (!p.frontend_capability_id) continue;
      const prev = perCap.get(p.frontend_capability_id);
      if (prev && rank(prev.coverage_level) >= rank(p.coverage_level)) continue;
      perCap.set(p.frontend_capability_id, p);
    }

    for (const [capId, p] of perCap) {
      if (!map[capId]) map[capId] = [];
      map[capId].push({
        vendor,
        productName: p.product_name,
        coverageLevel: p.coverage_level,
        notes: p.coverage_notes || '',
      });
    }
  }

  // Sort each capability's list by coverage strength (strongest first).
  for (const capId of Object.keys(map)) {
    map[capId].sort((a, b) => rank(b.coverageLevel) - rank(a.coverageLevel));
  }
  return map;
}

function rank(level) {
  // Higher = stronger coverage
  if (level === 'STRONG') return 3;
  if (level === 'PARTIAL') return 2;
  if (level === 'GAP') return 1;
  return 0;
}

/**
 * Case-insensitive client-side filter across name + aliases, used by the
 * picker when doing incremental filtering on a small result set.
 */
export function vendorMatchesQuery(vendor, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (vendor.name.toLowerCase().includes(q)) return true;
  return (vendor.aliases || []).some(a => a.toLowerCase().includes(q));
}
